import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PDFDownloadLink } from '@react-pdf/renderer';
import {
  ChefHat, ChevronDown, ChevronRight, Download, FileText, RefreshCw, Trophy, Utensils, Wine,
} from 'lucide-react';

import { Spinner } from '@/components/ui/Spinner';
import { getSalesReport, type ReportSection } from '@/services/admin';
import { formatRub } from '@/utils/format';

import { SalesReportPdf } from './reports/SalesReportPdf';

type Preset = 'today' | '7d' | '30d' | 'month' | 'custom';

function rangeFor(preset: Preset): { from?: string; to?: string } {
  const now = new Date();
  const day = (d: Date) => d.toISOString().slice(0, 10);
  if (preset === 'today') {
    return { from: day(now), to: day(now) };
  }
  if (preset === '7d') {
    const from = new Date(now);
    from.setDate(from.getDate() - 6);
    return { from: day(from), to: day(now) };
  }
  if (preset === '30d') {
    const from = new Date(now);
    from.setDate(from.getDate() - 29);
    return { from: day(from), to: day(now) };
  }
  if (preset === 'month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: day(from), to: day(now) };
  }
  return {};
}

export default function ReportsPage() {
  const [preset, setPreset] = useState<Preset>('month');
  const [custom, setCustom] = useState<{ from: string; to: string }>(() => {
    const r = rangeFor('month');
    return { from: r.from ?? '', to: r.to ?? '' };
  });

  const effective = useMemo(() => {
    if (preset === 'custom') return { from: custom.from || undefined, to: custom.to || undefined };
    return rangeFor(preset);
  }, [preset, custom]);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin/reports/sales', effective.from, effective.to],
    queryFn: () => getSalesReport(effective),
  });

  const exportCsv = () => {
    if (!data) return;
    const rows: string[][] = [
      ['Секция', 'Категория', 'Позиция', 'Кол-во', 'Сумма (₽)'],
    ];
    for (const sec of data.sections) {
      for (const cat of sec.categories) {
        for (const d of cat.dishes) {
          rows.push([
            sec.sectionLabel,
            cat.categoryName,
            d.name,
            String(d.quantity),
            (d.total / 100).toFixed(2),
          ]);
        }
      }
    }
    const csv = rows
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `sales-report-${Date.now()}.csv`;
    a.click();
  };

  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold">Отчёт по продажам</h1>
          <p className="text-sm text-slate-500">Бар / Кухня → категория → позиция</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} /> <span className="hidden sm:inline">Обновить</span>
          </button>
          <button className="btn-outline" onClick={exportCsv} disabled={!data}>
            <Download size={16} /> CSV
          </button>
          {data && (
            <PDFDownloadLink
              document={<SalesReportPdf report={data} />}
              fileName={`sales-report-${new Date().toISOString().slice(0, 10)}.pdf`}
              className="btn-primary"
            >
              {({ loading }) => (
                <>
                  <FileText size={16} /> {loading ? 'Собираем PDF…' : 'PDF'}
                </>
              )}
            </PDFDownloadLink>
          )}
        </div>
      </header>

      <div className="card p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex gap-2 flex-wrap">
          {([
            ['today', 'Сегодня'],
            ['7d', '7 дней'],
            ['30d', '30 дней'],
            ['month', 'Этот месяц'],
            ['custom', 'Произвольно'],
          ] as Array<[Preset, string]>).map(([k, label]) => (
            <button
              key={k}
              className={preset === k ? 'btn-primary' : 'btn-outline'}
              onClick={() => setPreset(k)}
            >
              {label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="flex gap-2 items-end">
            <div>
              <label className="label">С даты</label>
              <input
                type="date"
                className="input"
                value={custom.from}
                onChange={(e) => setCustom({ ...custom, from: e.target.value })}
              />
            </div>
            <div>
              <label className="label">По дату</label>
              <input
                type="date"
                className="input"
                value={custom.to}
                onChange={(e) => setCustom({ ...custom, to: e.target.value })}
              />
            </div>
          </div>
        )}
      </div>

      {isLoading || !data ? (
        <Spinner />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <StatCard label="Заказов за период" value={String(data.totals.ordersCount)} />
            <StatCard label="Продано позиций" value={String(data.totals.itemsQuantity)} />
            <StatCard label="Выручка" value={formatRub(data.totals.revenue)} accent />
          </div>

          {data.sections.length === 0 ? (
            <div className="card p-12 text-center text-slate-400">
              За выбранный период продаж нет
            </div>
          ) : (
            <div className="grid gap-4">
              {data.sections.map((sec) => (
                <SectionCard key={sec.section} section={sec} />
              ))}
            </div>
          )}

          {data.top.length > 0 && (
            <TopDishesCard top={data.top} />
          )}
        </>
      )}
    </div>
  );
}

function sectionLabel(section: string) {
  switch (section) {
    case 'KITCHEN': return 'Кухня';
    case 'BAR': return 'Бар';
    default: return 'Блюда';
  }
}

function StatCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`card p-5 ${accent ? 'border-indigo-200 bg-indigo-50/40' : ''}`}>
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function SectionCard({ section }: { section: ReportSection }) {
  const [open, setOpen] = useState(true);
  const Icon = section.section === 'KITCHEN'
    ? Utensils
    : section.section === 'BAR'
      ? Wine
      : ChefHat;
  const headBg = section.section === 'KITCHEN'
    ? 'bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100/70'
    : section.section === 'BAR'
      ? 'bg-cyan-50 text-cyan-900 border-cyan-200 hover:bg-cyan-100/70'
      : 'bg-emerald-50 text-emerald-900 border-emerald-200 hover:bg-emerald-100/70';

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full px-5 py-3 border-b flex items-center justify-between transition-colors ${headBg}`}
      >
        <div className="flex items-center gap-2 font-semibold">
          {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <Icon size={18} />
          {section.sectionLabel}
        </div>
        <div className="font-semibold tabular-nums">
          {section.quantity} шт · {formatRub(section.total)}
        </div>
      </button>
      {open && (
        <div className="divide-y divide-slate-100">
          {section.categories.map((cat) => (
            <div key={cat.categoryId ?? cat.categoryName} className="px-5 py-4">
              <div className="flex justify-between items-baseline mb-2">
                <div className="font-medium text-slate-800">{cat.categoryName}</div>
                <div className="text-sm text-slate-500 tabular-nums">
                  {cat.quantity} шт ·{' '}
                  <span className="font-semibold text-slate-900">{formatRub(cat.total)}</span>
                </div>
              </div>
              <div className="overflow-x-auto -mx-5 px-5">
              <table className="w-full min-w-[420px] text-sm table-fixed">
                <colgroup>
                  <col />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '140px' }} />
                </colgroup>
                <thead>
                  <tr className="text-slate-400 text-[11px] uppercase tracking-wide">
                    <th className="text-left py-1.5 font-medium"></th>
                    <th className="text-right py-1.5 font-medium">Кол-во</th>
                    <th className="text-right py-1.5 font-medium">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {cat.dishes.map((d, idx) => (
                    <tr
                      key={d.dishId + d.section}
                      className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}
                    >
                      <td className="py-2 px-2 rounded-l truncate">{d.name}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{d.quantity}</td>
                      <td className="py-2 px-2 text-right font-medium tabular-nums rounded-r">
                        {formatRub(d.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TopDishesCard({ top }: { top: Array<{
  dishId: string; section: string; sectionLabel?: string; name: string; categoryName: string; quantity: number; total: number;
}> }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="card overflow-hidden mt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 border-b flex items-center justify-between bg-indigo-50 text-indigo-900 border-indigo-200 hover:bg-indigo-100/70 transition-colors"
      >
        <div className="flex items-center gap-2 font-semibold">
          {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <Trophy size={18} />
          Топ позиций по выручке
        </div>
        <div className="font-semibold tabular-nums">{top.length}</div>
      </button>
      {open && (
        <div className="px-5 py-4 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm table-fixed">
            <colgroup>
              <col />
              <col style={{ width: '25%' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '140px' }} />
            </colgroup>
            <thead>
              <tr className="text-slate-400 text-[11px] uppercase tracking-wide">
                <th className="text-left py-1.5 font-medium"></th>
                <th className="text-left py-1.5 font-medium">Категория</th>
                <th className="text-left py-1.5 font-medium">Секция</th>
                <th className="text-right py-1.5 font-medium">Кол-во</th>
                <th className="text-right py-1.5 font-medium">Сумма</th>
              </tr>
            </thead>
            <tbody>
              {top.map((d, idx) => (
                <tr
                  key={d.dishId + d.section}
                  className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}
                >
                  <td className="py-2 px-2 truncate">{d.name}</td>
                  <td className="py-2 px-2 text-slate-500 truncate">{d.categoryName}</td>
                  <td className="py-2 px-2">{d.sectionLabel || sectionLabel(d.section)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{d.quantity}</td>
                  <td className="py-2 px-2 text-right font-medium tabular-nums">
                    {formatRub(d.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
